import {
	type ChangeEvent,
	type ClipboardEvent,
	type KeyboardEvent,
	useEffect,
	useRef,
} from 'react'
import { Plus, X } from 'lucide-react'
import styles from './SectionListEditor.module.css'

// The editor works on a looser "draft" shape than the schema: headings are
// plain strings ('' = unnamed) and rows may be empty mid-edit. `fromDrafts`
// prunes empties and nulls blank headings back to the schema shape; the schema
// (min 1 item per section, min 1 section) is enforced by the caller on save.
export type DraftSection = { heading: string; items: string[] }

type Section = { heading: string | null; items: string[] }

export const emptyDraft = (): DraftSection[] => [{ heading: '', items: [''] }]

export const toDrafts = (sections: Section[]): DraftSection[] =>
	sections.length === 0
		? emptyDraft()
		: sections.map((s) => ({
				heading: s.heading ?? '',
				items: [...s.items],
			}))

export const fromDrafts = (drafts: DraftSection[]): Section[] =>
	drafts
		.map((s) => ({
			heading: s.heading.trim(),
			items: s.items.map((i) => i.trim()).filter((i) => i.length > 0),
		}))
		.filter((s) => s.items.length > 0)
		.map((s) => ({
			heading: s.heading === '' ? null : s.heading,
			items: s.items,
		}))

type Props = {
	// Lower-case singular noun for the add-row button, e.g. "ingredient".
	itemNoun: string
	value: DraftSection[]
	onChange: (next: DraftSection[]) => void
	itemPlaceholder: string
	// Number the rows (instructions read as ordered steps).
	ordered?: boolean
	// Rows are paragraphs, not one-liners — use a box that grows with the
	// text. Enter still moves to the next row either way, so a row never
	// contains a line break.
	multiline?: boolean
	maxItemLength: number
}

export const SectionListEditor = ({
	itemNoun,
	value,
	onChange,
	itemPlaceholder,
	ordered = false,
	multiline = false,
	maxItemLength,
}: Props) => {
	// Row inputs keyed "si:ii" so an add/paste/Enter can focus the right one
	// after the controlled re-render.
	const inputs = useRef(
		new Map<string, HTMLInputElement | HTMLTextAreaElement>(),
	)
	const focusNext = useRef<string | null>(null)

	useEffect(() => {
		const key = focusNext.current
		if (key === null) return
		focusNext.current = null
		inputs.current.get(key)?.focus()
	})

	const setItem = (si: number, ii: number, text: string) =>
		onChange(
			value.map((s, i) =>
				i === si
					? {
							...s,
							items: s.items.map((it, j) =>
								j === ii ? text : it,
							),
						}
					: s,
			),
		)

	const setHeading = (si: number, text: string) =>
		onChange(value.map((s, i) => (i === si ? { ...s, heading: text } : s)))

	const addRow = (si: number) => {
		const sec = value[si]
		if (!sec) return
		onChange(
			value.map((s, i) =>
				i === si ? { ...s, items: [...s.items, ''] } : s,
			),
		)
		focusNext.current = `${si}:${sec.items.length}`
	}

	const removeRow = (si: number, ii: number) =>
		onChange(
			value.map((s, i) => {
				if (i !== si) return s
				const items = s.items.filter((_, j) => j !== ii)
				// Never leave a section with no rows — keep one empty row.
				return { ...s, items: items.length > 0 ? items : [''] }
			}),
		)

	const addSection = () => {
		onChange([...value, { heading: '', items: [''] }])
		focusNext.current = `${value.length}:0`
	}

	const removeSection = (si: number) => {
		const next = value.filter((_, i) => i !== si)
		onChange(next.length === 0 ? emptyDraft() : next)
	}

	const onKeyDown = (
		si: number,
		ii: number,
		e: KeyboardEvent<HTMLElement>,
	) => {
		if (e.key !== 'Enter') return
		// Insert a fresh row right after this one and jump into it, so a list
		// can be typed straight through without reaching for the mouse. This
		// is why a multiline row still can't hold a newline — Enter always
		// means "next row", never "line break".
		e.preventDefault()
		onChange(
			value.map((s, i) => {
				if (i !== si) return s
				const items = [...s.items]
				items.splice(ii + 1, 0, '')
				return { ...s, items }
			}),
		)
		focusNext.current = `${si}:${ii + 1}`
	}

	const onPaste = (
		si: number,
		ii: number,
		e: ClipboardEvent<HTMLElement>,
	) => {
		const text = e.clipboardData.getData('text')
		if (!text.includes('\n')) return // single line: let the browser paste it
		e.preventDefault()
		const lines = text
			.split(/\r?\n/)
			.map((l) => l.trim())
			.filter((l) => l.length > 0)
		if (lines.length === 0) return
		// Explode the pasted block into one row per line, replacing the row
		// pasted into. Focus lands on the last inserted row.
		onChange(
			value.map((s, i) => {
				if (i !== si) return s
				const items = [...s.items]
				items.splice(ii, 1, ...lines)
				return { ...s, items }
			}),
		)
		focusNext.current = `${si}:${ii + lines.length - 1}`
	}

	// A single unnamed section needs no heading input — a plain one-list recipe.
	// Headings appear once there's more than one section (or one already names).
	const showHeadings =
		value.length > 1 || value.some((s) => s.heading.trim() !== '')

	return (
		<div className={styles.editor}>
			{value.map((sec, si) => (
				<div key={si} className={styles.section}>
					{showHeadings && (
						<div className={styles.sectionHead}>
							<input
								className={styles.heading}
								value={sec.heading}
								placeholder="Section name, e.g. For the sauce"
								maxLength={80}
								onChange={(e) => setHeading(si, e.target.value)}
							/>
							<button
								type="button"
								className={styles.iconBtn}
								onClick={() => removeSection(si)}
								aria-label="Remove section"
							>
								<X size={16} aria-hidden="true" />
							</button>
						</div>
					)}
					<ol className={styles.rows}>
						{sec.items.map((item, ii) => {
							const field = {
								ref: (
									el:
										| HTMLInputElement
										| HTMLTextAreaElement
										| null,
								) => {
									const key = `${si}:${ii}`
									if (el) inputs.current.set(key, el)
									else inputs.current.delete(key)
								},
								className: styles.input,
								value: item,
								placeholder: itemPlaceholder,
								maxLength: maxItemLength,
								onChange: (
									e: ChangeEvent<
										HTMLInputElement | HTMLTextAreaElement
									>,
								) => setItem(si, ii, e.target.value),
								onKeyDown: (e: KeyboardEvent<HTMLElement>) =>
									onKeyDown(si, ii, e),
								onPaste: (e: ClipboardEvent<HTMLElement>) =>
									onPaste(si, ii, e),
							}
							return (
								<li
									key={ii}
									className={`${styles.row} ${
										multiline ? styles.rowMultiline : ''
									}`}
								>
									{ordered && (
										<span className={styles.num}>
											{ii + 1}
										</span>
									)}
									{multiline ? (
										<textarea
											{...field}
											className={`${styles.input} ${styles.textarea}`}
											rows={2}
										/>
									) : (
										<input {...field} />
									)}
									<button
										type="button"
										className={styles.iconBtn}
										onClick={() => removeRow(si, ii)}
										aria-label={`Remove ${itemNoun}`}
									>
										<X size={16} aria-hidden="true" />
									</button>
								</li>
							)
						})}
					</ol>
					<button
						type="button"
						className={styles.addRow}
						onClick={() => addRow(si)}
					>
						<Plus size={14} aria-hidden="true" /> Add {itemNoun}
					</button>
				</div>
			))}
			<button
				type="button"
				className={styles.addSection}
				onClick={addSection}
			>
				<Plus size={14} aria-hidden="true" /> Add section
			</button>
		</div>
	)
}
